import { useEffect } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import Button from "@atlaskit/button";
import Link from "@atlaskit/link";

import { useSiteActivationContext } from "@/components/signup/site-activation/hooks/site-activation-context";
import { useUserContext } from "@/components/user-context/user-context-provider";
import { wacToken } from "@/libraries/design/foundations";

export const AddPaymentMethodContent = () => {
    const { user } = useUserContext();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { paymentDetailsView, setPaymentDetailsView } =
        useSiteActivationContext();

    useEffect(() => {
        createAnalyticsEvent({
            action: "viewed",
            eventType: "screen",
            name: "startRovoDevTrialPaymentBlockingScreen",
            attributes: {
                userId: user?.accountId
            }
        }).fire();
    }, []);

    const adminPaymentLink =
        paymentDetailsView.view === "payment-required"
            ? paymentDetailsView.adminPaymentLink
            : undefined;

    const handleOpenPaymentMethods = () => {
        if (adminPaymentLink) {
            window.open(adminPaymentLink, "_blank");
            setPaymentDetailsView({ view: "payment" });

            createAnalyticsEvent({
                action: "clicked",
                eventType: "ui",
                actionSubject: "button",
                actionSubjectId: "openPaymentMethod",
                attributes: {
                    userId: user?.accountId
                }
            }).fire();
        }
    };

    const handleCloseAndGoBack = () => {
        setPaymentDetailsView({ view: "site-setup" });
    };

    return (
        <div css={containerCss}>
            <h1 css={titleCss}>
                <FormattedMessage
                    id="BxpSignup.AddPaymentMethod.Title"
                    defaultMessage="Add a payment method to start your trial"
                />
            </h1>
            <p css={introCss}>
                <FormattedMessage
                    id="BxpSignup.AddPaymentMethod.Intro"
                    defaultMessage="We couldn't find a payment method for your site, a billing admin must add one before you start a trial."
                />
            </p>

            <div css={cardsCss}>
                <div css={cardCss}>
                    <span css={iconWrapperCss} aria-hidden>
                        <ShieldIcon />
                    </span>
                    <strong css={cardTitleCss}>
                        <FormattedMessage
                            id="BxpSignup.AddPaymentMethod.NoChargeTitle"
                            defaultMessage="No charge until trial ends"
                        />
                    </strong>
                    <p css={cardBodyCss}>
                        <FormattedMessage
                            id="BxpSignup.AddPaymentMethod.NoChargeBody"
                            defaultMessage="There's no charge today, billing doesn't begin until the end of your trial."
                        />
                    </p>
                </div>
                {/* TODO: https://softwareteams.atlassian.net/browse/JOLT-2177
                    <div css={cardCss}>
                        <span css={iconWrapperCss} aria-hidden>
                            <ClockIcon />
                        </span>
                        <strong css={cardTitleCss}>
                            <FormattedMessage
                                id="BxpSignup.AddPaymentMethod.CancelAnytimeTitle"
                                defaultMessage="Cancel anytime"
                            />
                        </strong>
                        <p css={cardBodyCss}>
                            <FormattedMessage
                                id="BxpSignup.AddPaymentMethod.CancelAnytimeBody"
                                defaultMessage="If you want to end your trial, cancel anytime in Atlassian Administration."
                            />
                        </p>
                    </div> 
                */}
            </div>

            <h2 css={sectionTitleCss}>
                <FormattedMessage
                    id="BxpSignup.AddPaymentMethod.HowToTitle"
                    defaultMessage="How to add a payment method"
                />
            </h2>

            <ol css={stepsListCss}>
                <li css={stepCss}>
                    <span css={stepNumberCss}>1</span>
                    <div>
                        <strong>
                            <FormattedMessage
                                id="BxpSignup.AddPaymentMethod.Step1Title"
                                defaultMessage="Go to payment methods in Atlassian Administration"
                            />
                        </strong>
                        <p css={stepBodyCss}>
                            <FormattedMessage
                                id="BxpSignup.AddPaymentMethod.Step1Body"
                                defaultMessage="Billing admin role required to add payment methods."
                            />{" "}
                            <Link
                                href="https://support.atlassian.com/subscriptions-and-billing/docs/understand-billing-administration/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <FormattedMessage
                                    id="BxpSignup.AddPaymentMethod.ReadAboutPermissions"
                                    defaultMessage="Read about permissions"
                                />
                            </Link>
                        </p>
                        <Button
                            appearance="primary"
                            onClick={handleOpenPaymentMethods}
                            iconAfter={<ExternalLinkIcon />}
                        >
                            <FormattedMessage
                                id="BxpSignup.AddPaymentMethod.OpenPaymentMethods"
                                defaultMessage="Open payment methods"
                            />
                        </Button>
                    </div>
                </li>
                <li css={stepCss}>
                    <span css={stepNumberCss}>2</span>
                    <div>
                        <strong>
                            <FormattedMessage
                                id="BxpSignup.AddPaymentMethod.Step2Title"
                                defaultMessage="Enter your billing details"
                            />
                        </strong>
                        <p css={stepBodyCss}>
                            <FormattedMessage
                                id="BxpSignup.AddPaymentMethod.Step2Body"
                                defaultMessage="Add a billing address and payment method. We accept credit cards and PayPal."
                            />
                        </p>
                    </div>
                </li>
                <li css={stepCss}>
                    <span css={stepNumberCss}>3</span>
                    <div>
                        <strong>
                            <FormattedMessage
                                id="BxpSignup.AddPaymentMethod.Step3Title"
                                defaultMessage="Close the payment methods tab"
                            />
                        </strong>
                        <p css={stepBodyCss}>
                            <FormattedMessage
                                id="BxpSignup.AddPaymentMethod.Step3Body"
                                defaultMessage="When you're done, close the payment methods window and return here."
                            />
                        </p>
                    </div>
                </li>
                <li css={stepCss}>
                    <span css={stepNumberCss}>4</span>
                    <div>
                        <strong>
                            <FormattedMessage
                                id="BxpSignup.AddPaymentMethod.Step4Title"
                                defaultMessage="Return and set up your trial"
                            />
                        </strong>
                        <p css={stepBodyCss}>
                            <FormattedMessage
                                id="BxpSignup.AddPaymentMethod.Step4Body"
                                defaultMessage="After adding a payment method, return to where you started and set up your trial again."
                            />
                        </p>
                        <Button
                            appearance="default"
                            onClick={handleCloseAndGoBack}
                        >
                            <FormattedMessage
                                id="BxpSignup.AddPaymentMethod.CloseAndGoBack"
                                defaultMessage="Close and go back"
                            />
                        </Button>
                    </div>
                </li>
            </ol>
        </div>
    );
};

const ShieldIcon = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
    >
        <path
            d="M12 2L4 5v6.09c0 5.05 3.41 9.76 8 10.91 4.59-1.15 8-5.86 8-10.91V5l-8-3z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
        />
    </svg>
);

// TODO: https://softwareteams.atlassian.net/browse/JOLT-2177
// const ClockIcon = () => (
//     <svg
//         width="24"
//         height="24"
//         viewBox="0 0 24 24"
//         fill="none"
//         xmlns="http://www.w3.org/2000/svg"
//         aria-hidden
//     >
//         <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
//         <path
//             d="M12 6v6l4 2"
//             stroke="currentColor"
//             strokeWidth="2"
//             strokeLinecap="round"
//         />
//     </svg>
// );

const ExternalLinkIcon = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
    >
        <path
            d="M12 8.67V12a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1h3.33M9.33 2h4v4M14 2l-6 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const containerCss = css({
    maxWidth: 640,
    marginBlock: 0,
    marginInline: "auto",
    paddingBlock: 24,
    paddingInline: 24,
    textAlign: "left"
});

const titleCss = css({
    fontSize: 24,
    fontWeight: 700,
    lineHeight: 1.3,
    marginBlockStart: 0,
    marginBlockEnd: 12,
    textAlign: "center"
});

const introCss = css({
    color: wacToken("color.neutral.dark400"),
    fontSize: 16,
    lineHeight: 1.5,
    marginBlockStart: 0,
    marginBlockEnd: 24,
    textAlign: "center"
});

const cardsCss = css({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginBlockEnd: 32
});

const cardCss = css({
    paddingBlock: 16,
    paddingInline: 16,
    borderRadius: 8,
    backgroundColor: wacToken("color.neutral.light100"),
    border: `1px solid ${wacToken("color.neutral.dark200")}`
});

const iconWrapperCss = css({
    display: "inline-flex",
    marginBlockEnd: 8,
    color: wacToken("color.primary.blue")
});

const cardTitleCss = css({
    display: "block",
    fontSize: 14,
    fontWeight: 600,
    marginBlockEnd: 4
});

const cardBodyCss = css({
    fontSize: 14,
    lineHeight: 1.5,
    marginBlock: 0,
    marginInline: 0,
    color: wacToken("color.neutral.dark400")
});

const sectionTitleCss = css({
    fontSize: 18,
    fontWeight: 600,
    marginBlockStart: 0,
    marginBlockEnd: 16
});

const stepsListCss = css({
    listStyle: "none",
    marginBlock: 0,
    marginInline: 0,
    paddingBlock: 0,
    paddingInline: 0,
    display: "flex",
    flexDirection: "column",
    gap: 24
});

const stepCss = css({
    display: "flex",
    gap: 16,
    alignItems: "flex-start"
});

const stepNumberCss = css({
    flexShrink: 0,
    width: 28,
    height: 28,
    borderRadius: "50%",
    backgroundColor: wacToken("color.primary.blue"),
    color: wacToken("color.neutral.light0"),
    fontSize: 14,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center"
});

const stepBodyCss = css({
    marginBlockStart: 4,
    marginBlockEnd: 12,
    marginInline: 0,
    fontSize: 14,
    lineHeight: 1.5,
    color: wacToken("color.neutral.dark400")
});
